import React, { useState, useEffect } from 'react';
import useUserCredentials from '../../utils/hooks/user/useUserCredentials';
import axios from '../../utils/api/axios';
import toast from 'react-hot-toast';

interface VantaAuthResponse {
  message: string;
  user_id: string;
  source_id: string;
  auth_url: string;
  auto_generated: {
    source_id: string;
    pattern: string;
  };
  instructions: string[];
}

interface VantaStatusResponse {
  user_id: number;
  email: string;
  vanta_connected: boolean;
  vanta_account_id: string | null;
  has_tokens: boolean;
}

const VantaIntegration: React.FC = () => {
  const { id: userId } = useUserCredentials();
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);

  // Check Vanta connection status on component mount
  useEffect(() => {
    if (userId) {
      checkVantaStatus();
    }
  }, [userId]);

  const checkVantaStatus = async () => {
    if (!userId) {
      console.log('No userId available for status check');
      return;
    }

    setIsCheckingStatus(true);
    try {
      console.log('Checking Vanta status for user:', userId);
      const response = await axios.get(`/vanta/status/${userId}`);
      console.log('Vanta status response:', response.data);

      const data: VantaStatusResponse = response.data;
      setIsConnected(data.vanta_connected);
    } catch (error: any) {
      console.error('Failed to check Vanta status:', error);
      console.error('Status check error details:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status
      });
      setIsConnected(false);
    } finally {
      setIsCheckingStatus(false);
    }
  };

  const handleConnect = async () => {
    if (!userId) {
      toast.error('User not authenticated');
      return;
    }

    setIsLoading(true);
    try {
      console.log('Attempting to connect to Vanta for user:', userId);

      // Generate Vanta OAuth URL
      const response = await axios.get(`/vanta/auth/${userId}`);
      console.log('Vanta auth response:', response.data);

      const data: VantaAuthResponse = response.data;

      if (data.auth_url) {
        console.log('Opening Vanta OAuth URL in new tab:', data.auth_url);
        // Open Vanta OAuth URL in new tab
        window.open(data.auth_url, '_blank');
        toast.success('Vanta OAuth page opened in new tab');
      } else {
        throw new Error('No auth URL received from server');
      }

    } catch (error: any) {
      console.error('Failed to initiate Vanta connection:', error);
      console.error('Error details:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
        statusText: error?.response?.statusText
      });

      // More detailed error message
      let errorMessage = 'Failed to connect to Vanta';
      if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!userId) {
      toast.error('User not authenticated');
      return;
    }

    setIsLoading(true);
    try {
      console.log('Attempting to disconnect Vanta for user:', userId);

      // Call disconnect endpoint
      const response = await axios.post(`/vanta/disconnect/${userId}`);
      console.log('Vanta disconnect response:', response.data);

      if (response.data.success) {
        setIsConnected(false);
        toast.success('Successfully disconnected from Vanta');
      } else {
        throw new Error(response.data.message || 'Failed to disconnect');
      }

    } catch (error: any) {
      console.error('Failed to disconnect from Vanta:', error);
      console.error('Error details:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
        statusText: error?.response?.statusText
      });

      let errorMessage = 'Failed to disconnect from Vanta';
      if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full">
      <div className="group relative overflow-hidden bg-white border border-gray-200 rounded-xl p-5 transition-all hover:border-blue-200 hover:shadow-xl hover:shadow-blue-500/5">

        {/* Header with Status */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-blue-50 text-blue-600 ring-1 ring-blue-100 p-1.5">
              <img
                src="/vanta-logo.jpg"
                alt="Vanta"
                className="w-full h-full object-contain"
              />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-sm">Vanta</h3>
              <p className="text-[11px] text-gray-400 font-medium uppercase tracking-wider">Compliance Platform</p>
            </div>
          </div>

          <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-bold uppercase tracking-wider border transition-colors ${isConnected
              ? 'bg-green-50 text-green-700 border-green-200'
              : 'bg-gray-50 text-gray-500 border-gray-200'
            }`}>
            <span className={`w-1.5 h-1.5 rounded-full ${isCheckingStatus ? 'bg-blue-400 animate-spin border border-t-transparent' :
                isConnected ? 'bg-green-500 animate-pulse' : 'bg-gray-400'
              }`} />
            {isCheckingStatus ? 'Checking' : isConnected ? 'Active' : 'Not Connected'}
          </div>
        </div>

        {/* Description */}
        <p className="text-sm text-gray-600 mb-6 leading-relaxed">
          Sync your security data with Vanta for automated compliance monitoring and real-time audit readiness.
        </p>

        {/* Action Button */}
        <div className="pt-4 border-t border-gray-100 flex justify-start">
          {isConnected ? (
            <button
              onClick={handleDisconnect}
              disabled={isLoading}
              className="inline-flex items-center justify-center px-4 py-2 bg-white border border-red-200 text-red-600 rounded-lg text-xs font-bold hover:bg-red-50 disabled:opacity-50 transition-all shadow-sm"
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                  <span>Disconnecting...</span>
                </div>
              ) : (
                'Disconnect Vanta'
              )}
            </button>
          ) : (
            <button
              onClick={handleConnect}
              disabled={isLoading || isCheckingStatus}
              className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg text-xs font-bold hover:bg-blue-700 disabled:opacity-50 transition-all shadow-sm"
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Connecting...</span>
                </div>
              ) : (
                'Connect to Vanta'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default VantaIntegration;
